import {useCallback, useState} from 'react'
import {useLogsQuery} from './useLogs'
import {usePendingPrompt} from './use-pending-prompt'
import type {LogEntry, SessionState} from '../types/session'
import type {NormalizedPrompt} from '../components/session-logs/prompt-log-utils'

export interface UseCLIPromptStateOptions {
  /** The session ID to monitor for prompts */
  sessionId: string | undefined
  /** Whether to enable the hook (typically: isCLI && !!sessionId) */
  enabled: boolean
  /** Current session state. */
  sessionState?: SessionState
}

export interface CLIPromptState {
  /** The current pending CLI prompt, or null if none */
  pendingPrompt: NormalizedPrompt | null
  /** Whether logs are still loading */
  isLoading: boolean
  /**
   * True when the pending prompt is older than the resume window AND the
   * session is paused (`waiting_for_user`, i.e. CCA only).
   */
  isExpired: boolean
  /** Raw session log entries the hook fetched. May be `undefined` while logs are loading. */
  logs: LogEntry[] | undefined
  /**
   * Optimistically mark a prompt resolved.
   * Pair with {@link unmarkPromptResolved} on mutation failure to restore the prompt.
   */
  markPromptResolved: (toolCallId: string) => void
  /**
   * Roll back an optimistic resolution when the underlying mutation
   * fails so the prompt re-appears for the user to retry.
   */
  unmarkPromptResolved: (toolCallId: string) => void
  /** Dismiss the current prompt without marking it as resolved */
  dismissPrompt: () => void
}

/**
 * Hook that monitors a CLI session for pending prompts.
 */
export function useCLIPromptState({sessionId, enabled, sessionState}: UseCLIPromptStateOptions): CLIPromptState {
  const [resolvedPromptIds, setResolvedPromptIds] = useState<Set<string>>(() => new Set())

  const {data: logs, isLoading} = useLogsQuery({
    sessionId,
    enabled: enabled && !!sessionId,
  })

  const {pendingPrompt, isExpired} = usePendingPrompt(logs, resolvedPromptIds, sessionState)

  const markPromptResolved = useCallback((toolCallId: string) => {
    setResolvedPromptIds(prev => {
      if (prev.has(toolCallId)) return prev
      const next = new Set(prev)
      next.add(toolCallId)
      return next
    })
  }, [])

  const unmarkPromptResolved = useCallback((toolCallId: string) => {
    setResolvedPromptIds(prev => {
      if (!prev.has(toolCallId)) return prev
      const next = new Set(prev)
      next.delete(toolCallId)
      return next
    })
  }, [])

  const dismissPrompt = useCallback(() => {
    if (pendingPrompt) {
      setResolvedPromptIds(prev => new Set(prev).add(pendingPrompt.toolCallId))
    }
  }, [pendingPrompt])

  return {
    pendingPrompt,
    isLoading,
    isExpired,
    logs,
    markPromptResolved,
    unmarkPromptResolved,
    dismissPrompt,
  }
}
